<template>
  <WdsStack direction-column gap-sm class="tw-mb-4" data-test="compliance-review-summary">
    <WdsStack v-if="!hideHeader" align-center justify-space-between>
      <WdsHeading type="title3">{{ $t('Compliance') }}</WdsHeading>
      <WdsText
        v-if="applicableCount"
        sm
        secondary
        class="tw-tabular-nums"
        data-test="compliance-review-progress"
      >
        {{ $t(':done of :total complete', { done: completedCount ?? 0, total: applicableCount }) }}
      </WdsText>
    </WdsStack>

    <WdsCallout
      v-model:expanded="expanded"
      class="compliance-review-callout"
      :tertiary="tone.tertiary"
      :success="tone.success"
      :warning="tone.warning"
      :icon="tone.icon"
      :toggle-label="$t('Details')"
      :toggle-label-expanded="$t('Hide')"
      data-test="compliance-review-status"
    >
      {{ lead }}
      <strong v-if="reviewer && hasDecision" data-test="compliance-review-reviewer">{{ reviewer.name }}</strong>
      <template v-if="hasDetails" #details>
        <div class="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-3">
          <div v-if="reviewedAt" class="tw-flex tw-flex-col" data-test="compliance-review-date">
            <WdsText sm secondary>{{ dateLabel }}</WdsText>
            <WdsText sm class="tw-tabular-nums">{{ reviewedAt }}</WdsText>
          </div>
          <div v-if="nextReviewAt" class="tw-flex tw-flex-col" data-test="compliance-review-next-review">
            <WdsText sm secondary>{{ $t('Next review') }}</WdsText>
            <WdsText sm class="tw-tabular-nums">{{ nextReviewAt }}</WdsText>
          </div>
          <div v-if="note" class="tw-col-span-2 tw-flex tw-flex-col" data-test="compliance-review-note">
            <WdsText sm secondary>{{ $t('Note') }}</WdsText>
            <ClampedText :text="note" />
          </div>
        </div>
      </template>
    </WdsCallout>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsCallout, WdsHeading, WdsStack, WdsText } from '@worksome/wds-components'
import ClampedText from '@/components/Common/ClampedText.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { VerifiedWorkerComplianceStatus } from '@/gql/graphql'

// Only the latest review is exposed, so this is a summary of that one.
const props = defineProps<{
  status: VerifiedWorkerComplianceStatus
  reviewer?: { id: string; name: string; avatar?: string | null } | null
  reviewedAt?: string | null
  nextReviewAt?: string | null
  note?: string | null
  completedCount?: number | null
  applicableCount?: number | null
  // The heading and count sit elsewhere, such as under the modal title.
  hideHeader?: boolean
}>()

// A request for changes opens with its note showing, since the worker still has something to do.
const expanded = ref(props.status === VerifiedWorkerComplianceStatus.ChangesRequested)

const hasDecision = computed(
  () =>
    props.status === VerifiedWorkerComplianceStatus.Cleared ||
    props.status === VerifiedWorkerComplianceStatus.ChangesRequested,
)

const hasDetails = computed(() => !!props.reviewedAt || !!props.nextReviewAt || !!props.note)

const tone = computed(() => {
  switch (props.status) {
    case VerifiedWorkerComplianceStatus.Cleared:
      return { tertiary: false, success: true, warning: false, icon: 'checkmark-filled' as const }
    case VerifiedWorkerComplianceStatus.ChangesRequested:
      return { tertiary: false, success: false, warning: true, icon: 'undo' as const }
    case VerifiedWorkerComplianceStatus.ReadyToReview:
      return { tertiary: false, success: false, warning: false, icon: 'view' as const }
    default:
      return { tertiary: true, success: false, warning: false, icon: 'circle-dash' as const }
  }
})

const lead = computed(() => {
  switch (props.status) {
    case VerifiedWorkerComplianceStatus.Cleared:
      return props.reviewer ? $t('Cleared by') : $t('Cleared')
    case VerifiedWorkerComplianceStatus.ChangesRequested:
      return props.reviewer ? $t('Changes requested by') : $t('Changes requested')
    case VerifiedWorkerComplianceStatus.ReadyToReview:
      return $t('Ready to review')
    default:
      return props.reviewedAt ? $t('Incomplete') : $t('Not reviewed yet')
  }
})

const dateLabel = computed(() => {
  switch (props.status) {
    case VerifiedWorkerComplianceStatus.Cleared:
      return $t('Cleared on')
    case VerifiedWorkerComplianceStatus.ChangesRequested:
      return $t('Requested on')
    default:
      return $t('Last reviewed on')
  }
})
</script>

<style scoped>
/* Tailwind preflight is off, so the details divider gets default borders on its other three sides. */
.compliance-review-callout :deep(.wds-callout-details) {
  border-width: 0;
  border-top-width: 1px;
}
</style>
